import React, { useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';

interface ContextMenuProps {
  x: number;
  y: number;
  item: any;
  type: 'file' | 'folder';
  onClose: () => void;
  onOpen: () => void;
  onRename?: () => void;
  onCopy?: () => void;
  onMove?: () => void;
  onDownload?: () => void;
  onPreview?: () => void;
  onShare?: () => void;
  onDelete?: () => void;
  onRestore?: () => void;
  onStar?: () => void;
  onDetails?: () => void;
  onMetadata?: () => void;
}

const ContextMenu: React.FC<ContextMenuProps> = ({
  x,
  y,
  item,
  type,
  onClose,
  onOpen,
  onRename,
  onCopy,
  onMove,
  onDownload,
  onPreview,
  onShare,
  onDelete,
  onRestore,
  onStar,
  onDetails,
  onMetadata
}) => {
  const menuRef = useRef<HTMLDivElement>(null);
  
  // Role-based permissions
  const canAccess = useSelector((state: any) => state.auth.permissions?.opsmanual);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        onClose();
      }
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    const handleScroll = () => {
      // Close context menu when user scrolls
      onClose();
    };

    const handleResize = () => {
      // Close context menu when window is resized
      onClose();
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    document.addEventListener('scroll', handleScroll, true); // Use capture to catch all scroll events
    window.addEventListener('resize', handleResize);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
      document.removeEventListener('scroll', handleScroll, true);
      window.removeEventListener('resize', handleResize);
    };
  }, [onClose]);

  // Adjust position if menu would go off screen
  useEffect(() => {
    if (menuRef.current) {
      const rect = menuRef.current.getBoundingClientRect();
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;

      let adjustedX = x;
      let adjustedY = y;

      // Check if menu goes off the right edge (common with action button clicks)
      if (rect.right > viewportWidth - 20) { // Add more margin for scrollbars
        // Position menu to the left of the cursor instead of right
        adjustedX = x - rect.width - 5;
        
        // If positioning to the left would go off the left edge, position at right edge with margin
        if (adjustedX < 10) {
          adjustedX = viewportWidth - rect.width - 30; // Account for scrollbar
        }
      }

      // Check if menu goes off the bottom edge (including footer space)
      const footerHeight = 60; // Approximate footer height
      const availableHeight = viewportHeight - footerHeight;
      
      if (rect.bottom > availableHeight) {
        // Position menu above the cursor instead of below
        adjustedY = y - rect.height - 5;
        
        // If positioning above would go off the top, position at the top with some margin
        if (adjustedY < 10) {
          adjustedY = 10;
        }
      }

      // Ensure menu doesn't go off the left edge
      if (adjustedX < 10) {
        adjustedX = 10;
      }

      // Ensure menu doesn't go off the top edge
      if (adjustedY < 10) {
        adjustedY = 10;
      }

      menuRef.current.style.left = `${adjustedX}px`;
      menuRef.current.style.top = `${adjustedY}px`;
    }
  }, [x, y]);

  const handleAction = (action: () => void) => {
    action();
    onClose();
  };

  return (
    <div
      ref={menuRef}
      className="dropbox-context-menu"
      style={{ left: x, top: y }}
    >
      {!item.is_deleted ? (
        <>
          {/* PRIMARY ACTIONS */}
          {type === 'folder' && (
            <div className="dropbox-context-menu-item" onClick={() => handleAction(onOpen)}>
              <i className="fas fa-folder-open"></i>
              <span>Open</span>
            </div>
          )}

          {onPreview && type === 'file' && canAccess?.['preview-document'] && (
            <div className="dropbox-context-menu-item" onClick={() => handleAction(onPreview)}>
              <i className="fas fa-eye"></i>
              <span>Preview</span>
            </div>
          )}

          {onDownload && type === 'file' && canAccess?.download && (
            <div className="dropbox-context-menu-item" onClick={() => handleAction(onDownload)}>
              <i className="fas fa-download"></i>
              <span>Download</span>
            </div>
          )}

          {/* QUICK ACTIONS */}
          {onStar && (
            <div className="dropbox-context-menu-item" onClick={() => handleAction(onStar)}>
              <i className={`${item.is_starred ? 'fas' : 'far'} fa-star`}></i>
              <span>{item.is_starred ? 'Remove from favourites' : 'Add to favourites'}</span>
            </div>
          )}

          {/* SEPARATOR */}
          <div className="dropbox-context-menu-divider"></div>

          {/* EDIT ACTIONS GROUP */}
          {onRename && canAccess?.rename && (
            <div className="dropbox-context-menu-item" onClick={() => handleAction(onRename)}>
              <i className="fas fa-edit"></i>
              <span>Rename</span>
            </div>
          )}

          {onCopy && canAccess?.copy && (
            <div className="dropbox-context-menu-item" onClick={() => handleAction(onCopy)}>
              <i className="fas fa-copy"></i>
              <span>Make a copy</span>
            </div>
          )}

          {onMove && canAccess?.move && (
            <div className="dropbox-context-menu-item" onClick={() => handleAction(onMove)}>
              <i className="fas fa-arrows-alt"></i>
              <span>Move</span>
            </div>
          )}

          {onMetadata && canAccess?.['edit-metadata'] && (
            <div className="dropbox-context-menu-item" onClick={() => handleAction(onMetadata)}>
              <i className="fas fa-tags"></i>
              <span>Edit Metadata</span>
            </div>
          )}

          {/* SEPARATOR */}
          <div className="dropbox-context-menu-divider"></div>

          {/* INFORMATION ACTIONS */}
          {onDetails && (
            <div className="dropbox-context-menu-item" onClick={() => handleAction(onDetails)}>
              <i className="fas fa-info-circle"></i>
              <span>Details</span>
            </div>
          )}

          {/* SEPARATOR */}
          <div className="dropbox-context-menu-divider"></div>

          {/* DESTRUCTIVE ACTIONS */}
          {onDelete && canAccess?.delete && (
            <div className="dropbox-context-menu-item danger" onClick={() => handleAction(onDelete)}>
              <i className="fas fa-trash"></i>
              <span>Delete</span>
            </div>
          )}
        </>
      ) : (
        <>
          {onRestore && canAccess?.['restore-document'] && (
            <div className="dropbox-context-menu-item" onClick={() => handleAction(onRestore)}>
              <i className="fas fa-undo"></i>
              <span>Restore</span>
            </div>
          )}

          {onDelete && canAccess?.delete && (
            <div className="dropbox-context-menu-item danger" onClick={() => handleAction(onDelete)}>
              <i className="fas fa-trash-alt"></i>
              <span>Delete Permanently</span>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default ContextMenu;
